// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { distanceMeters, type LatLon } from '@/features/hole/holeview/tileMath';

/**
 * The points a hole bends at, between the tee and the green.
 *
 * A hole is played in shots, and the turning points are where one shot ends and the next
 * aims somewhere else: a par 4 has one, a par 5 has two. That is the same split
 * `FairwayMidpoints` measures the fairway at server-side — this is the admin saying where
 * the hole actually turns, rather than the imagery guessing.
 */

/**
 * How many turning points a hole of this par is split by: par 3 none, par 4 one, par 5 two.
 *
 * Clamped at four (a par 7) because par arrives from imported scorecards, and a hole
 * recorded as par 40 would otherwise put thirty-seven draggable dots on the map.
 */
export const turnPointCount = (par: number | null | undefined): number =>
  typeof par === 'number' && Number.isFinite(par) && par > 3 ? Math.min(Math.floor(par) - 3, 4) : 0;

const lerp = (from: LatLon, to: LatLon, fraction: number): LatLon => ({
  lat: from.lat + (to.lat - from.lat) * fraction,
  lon: from.lon + (to.lon - from.lon) * fraction,
});

/**
 * Where every turning point sits: the ones somebody placed, and the rest spread evenly along
 * the straight line between their placed neighbours.
 *
 * The rule is one sentence — an unplaced point lies on the line between the nearest thing on
 * either side that <em>is</em> placed, the tee and the green counting as placed. So a par 5
 * nobody has touched puts its two points at the thirds of the chord; drag the first one and
 * the second slides to the middle of what is left, from the new first point to the green,
 * until it is dragged in its own right. That is what keeps a half-edited hole a plausible
 * line rather than a zig-zag through a point the admin has not looked at yet.
 *
 * Interpolated in plain lat/lon: over the few hundred metres of one hole the difference from
 * a great-circle split is millimetres, and the placed points are what the line is really
 * made of.
 */
export const resolveTurnPoints = (
  tee: LatLon,
  green: LatLon,
  count: number,
  placed: ReadonlyMap<number, LatLon>
): LatLon[] => {
  const anchorAt = (index: number): LatLon | null =>
    index < 0 ? tee : index >= count ? green : placed.get(index) ?? null;

  const points: LatLon[] = [];
  for (let index = 0; index < count; index += 1) {
    const own = placed.get(index);
    if (own) {
      points.push(own);
      continue;
    }
    let before = index - 1;
    while (anchorAt(before) === null) before -= 1;
    let after = index + 1;
    while (anchorAt(after) === null) after += 1;
    points.push(
      lerp(anchorAt(before)!, anchorAt(after)!, (index - before) / (after - before))
    );
  }
  return points;
};

/**
 * The line the hole is played along, tee end first, or an empty path when either end is
 * missing. Two points on a hole with no turning points, which is the chord it always was.
 */
export const holePath = (
  tee: LatLon | null,
  green: LatLon | null,
  count: number,
  placed: ReadonlyMap<number, LatLon>
): LatLon[] =>
  tee && green ? [tee, ...resolveTurnPoints(tee, green, count, placed), green] : [];

/** How long that line is on the ground. Zero for a path with nothing to measure. */
export const pathLengthMeters = (path: readonly LatLon[]): number =>
  path.reduce(
    (total, point, index) => (index === 0 ? 0 : total + distanceMeters(path[index - 1], point)),
    0
  );
